import React from 'react';
import axios from 'axios';
import { getApiToken } from '../../services/api';

/**
 * Wraps the whole app. Jenn, 2026-09-02 (live member night): "it's hard
 * to get members to go to the system at all, so reducing all friction or
 * risk is a big deal." Every real bug found today (Paula, the Members
 * page, the privacy display) shared one root cause underneath their
 * individual fixes: an uncaught render error blanked the entire screen
 * with zero feedback to the member and zero record anywhere — the only
 * reason any of them surfaced was someone happening to say something.
 *
 * This does two things: shows a real, branded "something went wrong"
 * screen instead of a blank one, and reports the crash to the server so
 * Team365 can see it without needing a member to report it first. Posted
 * with a bare axios call, not the app's own configured api client — that
 * client's own interceptors/base URL logic could itself be implicated in
 * whatever just crashed, and a crash reporter that can be taken down by
 * the same bug it's reporting defeats the purpose.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    try {
      const token = getApiToken();
      axios.post('/api/client-errors', {
        message: error?.message || String(error),
        stack: (error?.stack || '') + '\n' + (info?.componentStack || ''),
        route: window.location?.pathname,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        timeout: 5000,
      }).catch(() => { /* reporting itself must never throw */ });
    } catch { /* never let the reporter crash the crash handler */ }
  }

  handleReload = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-reset-cream px-4">
          <div className="w-full max-w-md text-center space-y-4">
            <div className="text-4xl">🌿</div>
            <h2 className="text-xl font-bold text-reset-brown">Something didn't load right</h2>
            <p className="text-sm text-gray-600">
              This has already been reported so Team365 can look into it. Try going back to your dashboard —
              if it keeps happening, reach out and let us know.
            </p>
            <button
              onClick={this.handleReload}
              className="inline-block bg-reset-terracotta text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
